package com.tovtalk.data.repository

import com.tovtalk.data.mapper.CommentMapper
import com.tovtalk.data.remote.ApiClient
import com.tovtalk.data.remote.ApiEndpoints
import com.tovtalk.data.remote.apiCall
import com.tovtalk.data.remote.dto.CommentDto
import com.tovtalk.data.remote.dto.ReplyCommentDto
import com.tovtalk.data.remote.dto.V2PaginatedData
import com.tovtalk.domain.entity.Comment
import com.tovtalk.domain.entity.ReplyComment
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.CommentRepository
import io.ktor.client.request.*

/**
 * Comment Repository Implementation
 */
class CommentRepositoryImpl(
    private val apiClient: ApiClient
) : CommentRepository {

    // Server uses last_id (cursor) pagination, but the public API exposes a
    // page index. We translate by tracking the server-provided next_cursor per
    // post/comment: page <= 1 starts fresh; later pages send the stored cursor.
    private val commentCursors = mutableMapOf<Int, String?>()
    private val replyCursors = mutableMapOf<Int, String?>()

    override suspend fun getPostComments(postId: Int, page: Int): DomainResult<List<Comment>> {
        val cursor = if (page <= 1) null else commentCursors[postId]
        // First page with no prior cursor and a request for a later page means we've
        // already exhausted the list (server returned no next_cursor) — return empty.
        if (page > 1 && cursor == null && commentCursors.containsKey(postId)) {
            return DomainResult.Success(emptyList())
        }
        return apiCall<V2PaginatedData<CommentDto>, List<Comment>>(
            request = {
                apiClient.httpClient.get("${ApiEndpoints.POST_COMMENTS}$postId") {
                    parameter("limit", 20)
                    cursor?.let { parameter("cursor", it) }
                }
            },
            map = { data ->
                commentCursors[postId] = data.nextCursor
                data.items.map { CommentMapper.mapToEntity(it) }
            }
        )
    }

    override suspend fun getCommentReplies(commentId: Int, page: Int): DomainResult<List<ReplyComment>> {
        val cursor = if (page <= 1) null else replyCursors[commentId]
        if (page > 1 && cursor == null && replyCursors.containsKey(commentId)) {
            return DomainResult.Success(emptyList())
        }
        return apiCall<V2PaginatedData<ReplyCommentDto>, List<ReplyComment>>(
            request = {
                apiClient.httpClient.get("${ApiEndpoints.COMMENT_REPLIES}$commentId/replies") {
                    parameter("limit", 20)
                    cursor?.let { parameter("cursor", it) }
                }
            },
            map = { data ->
                replyCursors[commentId] = data.nextCursor
                data.items.map { CommentMapper.mapReplyToEntity(it) }
            }
        )
    }

    override suspend fun addComment(postId: Int, text: String?, gifUrl: String?): DomainResult<Comment> =
        apiCall<CommentDto, Comment>(
            request = {
                apiClient.httpClient.post("${ApiEndpoints.ADD_COMMENT}$postId") {
                    setBody(mapOf(
                        "comment_text" to text,
                        "gif_url" to gifUrl
                    ))
                }
            },
            map = { CommentMapper.mapToEntity(it) },
            emptyError = "Failed to add comment"
        )

    override suspend fun addReply(commentId: Int, text: String?, gifUrl: String?): DomainResult<ReplyComment> =
        apiCall<ReplyCommentDto, ReplyComment>(
            request = {
                apiClient.httpClient.post("${ApiEndpoints.COMMENT_REPLIES}$commentId/replies") {
                    setBody(mapOf(
                        "comment_text" to text,
                        "gif_url" to gifUrl
                    ))
                }
            },
            map = { CommentMapper.mapReplyToEntity(it) },
            emptyError = "Failed to add reply"
        )

    override suspend fun deleteComment(commentId: Int): DomainResult<Unit> =
        apiCall<Unit, Unit>(
            request = { apiClient.httpClient.delete("${ApiEndpoints.DELETE_COMMENT}$commentId") },
            map = { }
        )

    override suspend fun likeComment(commentId: Int): DomainResult<Unit> =
        apiCall<Unit, Unit>(
            request = { apiClient.httpClient.post("${ApiEndpoints.LIKE_COMMENT}$commentId/like") },
            map = { }
        )

    override suspend fun unlikeComment(commentId: Int): DomainResult<Unit> =
        apiCall<Unit, Unit>(
            request = { apiClient.httpClient.delete("${ApiEndpoints.UNLIKE_COMMENT}$commentId/like") },
            map = { }
        )

    override suspend fun likeReply(replyId: Int): DomainResult<Unit> =
        apiCall<Unit, Unit>(
            request = { apiClient.httpClient.post("${ApiEndpoints.COMMENT_REPLIES}$replyId/like") },
            map = { }
        )

    override suspend fun unlikeReply(replyId: Int): DomainResult<Unit> =
        apiCall<Unit, Unit>(
            request = { apiClient.httpClient.delete("${ApiEndpoints.COMMENT_REPLIES}$replyId/like") },
            map = { }
        )

}
